import DoneIcon from '@mui/icons-material/Done';
import HelpIcon from '@mui/icons-material/HelpOutline';
import { Box, IconButton, Step, StepLabel, Stepper } from '@mui/material';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import FundOpportunity from '../../models/FundOpportunity';
import { ContentSection } from '../ContentSection';
import { StyledTypography } from '../styles/styles';
import StyledModal from '../styles/StyledModal';
import StyledSection from '../styles/StyledSection';
import {
  getActiveStepFromStatus,
  getLifecycleStepTitle,
  lifecycleInfoBlocks,
  statusSteps,
} from './fundOpportunityHeaderUtils';

const CustomConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 'calc(50% + 0.5rem)',
    transform: 'translateY(-50%)',
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#2c6252',
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

type CustomStepIconWithLabelProps = {
  title: string;
  status: string;
  active: boolean;
  completed: boolean;
  printMode?: boolean;
};

function CustomStepIconWithLabel({
  title,
  status,
  active,
  completed,
  printMode,
}: CustomStepIconWithLabelProps) {
  const backgroundColor = completed ? '#6f7d8b' : active ? '#0a4f3c' : '#e0e0e0';

  return (
    <Box display="flex" flexDirection="column" alignItems="center" width="100%">
      <StyledTypography
        mb="0.25rem"
        sx={{
          fontSize: {
            sm: '0.625rem',
            md: '0.75rem',
          },
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          maxWidth: { xs: 50, sm: 70, md: 100 },
        }}
      >
        {title}
      </StyledTypography>

      <Box
        className={active ? 'stepper-icon-container active' : 'stepper-icon-container'}
        sx={{
          width: { xs: 24, sm: 30, md: 36 },
          height: { xs: 24, sm: 30, md: 36 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          backgroundColor,
          zIndex: 1,
          border: printMode ? '1px solid #0a4f3c' : undefined,
        }}
      >
        {completed && <DoneIcon sx={{ color: printMode ? '#0a4f3c' : '#fff' }} />}
      </Box>

      <StyledTypography
        mt="0.25rem"
        sx={{
          fontSize: {
            xs: 'clamp(0.5rem, 2vw, 0.625rem)',
            sm: 'clamp(0.5rem, 1.5vw, 0.75rem)',
            md: 'clamp(0.625rem, 1vw, 0.875rem)',
          },
          textAlign: 'center',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          maxWidth: { xs: 50, sm: 70, md: 100 },
        }}
      >
        {status}
      </StyledTypography>
    </Box>
  );
}

type LifecycleStepperProps = {
  status: FundOpportunity['status'];
  printMode?: boolean;
};

function LifecycleStepper({ status, printMode }: LifecycleStepperProps) {
  const activeStep = getActiveStepFromStatus(status);

  return (
    <Box width="100%">
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        connector={<CustomConnector />}
        sx={{
          width: '100%',
          '& .MuiStep-root': {
            padding: { xs: '0 2px', sm: '0 8px' },
          },
        }}
      >
        {statusSteps.map((step, index) => (
          <Step key={step.title}>
            <StepLabel
              icon={null}
              StepIconComponent={() => (
                <CustomStepIconWithLabel
                  title={step.title}
                  status={step.status ?? ''}
                  active={activeStep === index}
                  completed={activeStep > index}
                  printMode={printMode}
                />
              )}
            />
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}

type FundOpportunityLifecycleSectionProps = {
  status: FundOpportunity['status'];
  printMode?: boolean;
};

export default function FundOpportunityLifecycleSection({
  status,
  printMode,
}: FundOpportunityLifecycleSectionProps) {
  const [showStatusInfo, setShowStatusInfo] = useState(false);

  return (
    <>
      <Box
        className="summary-content"
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        alignItems="stretch"
        justifyContent="space-between"
        gap={{ xs: '1.875rem', lg: '7.5rem' }}
        mb="0.25rem"
        sx={{
          px: { xs: 0, md: '1.25rem' },
          pb: '1.25rem',
          ['@media (max-width: 1024px)']: {
            width: '100%',
            display: 'grid',
            rowGap: '1.875rem',
            gridTemplateColumns: '100%',
            gridAutoRows: '1fr',
            boxSizing: 'border-box',
          },
        }}
      >
        <StyledSection
          child
          tone="tone3"
          sx={{
            flex: 1,
            minWidth: 0,
            display: { xs: printMode ? 'flex' : 'none', md: 'flex' },
            flexDirection: 'column',
            gap: 1.5,
            position: 'relative',
          }}
        >
          <Box
            height="100%"
            flex={1}
            display="flex"
            flexDirection="column"
            className="stepper-parent"
            justifyContent="center"
            sx={(theme) => ({
              [theme.breakpoints.down('md')]: {
                display: printMode ? undefined : 'none',
              },
            })}
          >
            <LifecycleStepper status={status} printMode={printMode} />
          </Box>
          <IconButton
            onClick={() => setShowStatusInfo(true)}
            sx={{
              position: 'absolute',
              display: printMode ? 'none' : undefined,
              top: 0,
              right: 0,
            }}
          >
            <HelpIcon />
          </IconButton>
        </StyledSection>

        <Box
          className="summary-content"
          display={{ xs: printMode ? 'none' : 'flex', md: 'none' }}
          flexDirection="column"
          alignItems="stretch"
          justifyContent="space-between"
          gap={{ xs: '1.875rem', lg: '7.5rem' }}
          mb="0.25rem"
          sx={{
            px: { xs: 0, md: '1.25rem' },
            pb: '1.25rem',
            ['@media (max-width: 1024px)']: {
              width: '100%',
              display: 'grid',
              rowGap: '1.875rem',
              gridTemplateColumns: '100%',
              gridAutoRows: '1fr',
              boxSizing: 'border-box',
            },
          }}
        >
          <StyledSection
            child
            tone="tone2"
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.8,
            }}
          >
            <Box
              key="mobile-status"
              display={printMode ? 'none' : 'grid'}
              gridTemplateColumns="minmax(120px, 40%) 1fr"
              gap={2}
              alignItems="start"
            >
              <StyledTypography fontWeight={600}>Status:</StyledTypography>
              <StyledTypography>{getLifecycleStepTitle(status)}</StyledTypography>
            </Box>
          </StyledSection>
        </Box>
      </Box>

      <StyledModal
        open={showStatusInfo}
        onClose={() => setShowStatusInfo(false)}
        fullWidth
        maxWidth="lg"
        scroll="body"
      >
        <ContentSection
          noTopPadding
          noSidePaddingFrom="xs"
          tone="tone1"
          title="Fides Investment Opportunity Lifecycle"
          blocks={lifecycleInfoBlocks}
        />
      </StyledModal>
    </>
  );
}
